/**
 * `<caos-date-field>` — calendar-date input with a native date affordance.
 * (Catalog: Fields & input › DateField — calendar-date input with a native date affordance.)
 *
 * Same states as TextField (label, placeholder, disabled, invalid + message,
 * required, accent focus ring); the control is a native `type="date"` so the
 * platform calendar picker and keyboard entry come for free. See `base-field.ts`
 * for the shared template and the `::part(label|input|message)` seams.
 *
 * CAOS-600 — THE TEMPORAL KINDS ARE FORMATS OF THIS FIELD, NOT THREE FIELDS. `format` picks which
 * of the platform's temporal controls is drawn: `date` (the default), `time` for a time of day, and
 * `datetime` for a day and a time together. All three are the same field — same label, same value
 * attribute, same validity, same message — asking the platform for the affordance that matches
 * what the value IS.
 *
 * This is the editor the incumbent survey names as the real gap behind its formatted-time display:
 * the kernel has stored a zoneless `Time` since the temporal set was completed, and nothing could
 * edit one. The field renderer showed such a value read-only rather than hand it to a date picker
 * that would have thrown the time away.
 *
 * The same attribute closes a second, quieter hole. A `DateTime` was edited by a date-only control,
 * which saved midnight over whatever time the value carried — a silent data loss on every edit of
 * an unchanged field. `datetime` is what that kind should always have been drawn with.
 *
 * An unrecognised format reads as `date`, so a format nothing here can draw degrades to a calendar
 * rather than to a control with no affordance at all — the same rule the picklist's presentations
 * follow.
 */
import { CaosBaseField } from './base-field.js';
import { define } from '../base-element.js';

/** The temporal presentations, and the native control each one asks the platform for. */
export const DATE_FIELD_INPUT_TYPE = {
  date: 'date',
  time: 'time',
  datetime: 'datetime-local',
} as const;

export type CaosDateFieldFormat = keyof typeof DATE_FIELD_INPUT_TYPE;

export const DATE_FIELD_FORMATS = Object.keys(DATE_FIELD_INPUT_TYPE) as readonly CaosDateFieldFormat[];

export class CaosDateField extends CaosBaseField {
  static override get observedAttributes(): string[] {
    return [...super.observedAttributes, 'format'];
  }

  constructor() {
    super('date');
  }

  /** Which temporal control is drawn. Anything unrecognised reads as `date`. */
  get format(): CaosDateFieldFormat {
    const declared = this.getAttribute('format');
    return declared !== null && declared in DATE_FIELD_INPUT_TYPE ? (declared as CaosDateFieldFormat) : 'date';
  }
  set format(f: CaosDateFieldFormat) {
    if (f === 'date') this.removeAttribute('format');
    else this.setAttribute('format', f);
  }

  protected override sync(): void {
    // Before the shared pass, so the `value` it writes lands on the control that can hold it: a
    // `time` string set on a `date` input is rejected by the platform and silently becomes empty.
    this.setInputType(DATE_FIELD_INPUT_TYPE[this.format]);
    super.sync();
  }
}

define('caos-date-field', CaosDateField);

declare global {
  interface HTMLElementTagNameMap {
    'caos-date-field': CaosDateField;
  }
}
